import React, { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { entrarNaMetaPeloToken } from '../services/api';
import { limparConviteDeMeta } from '../utils/conviteDeMeta';
import { PaginaDeConviteDeMeta } from './ConvitePublico';
import EntradaComSenha from './EntradaComSenha';
import { LOGIN_COM_SENHA } from '../utils/loginComSenha';

/**
 * A fiação do convite de meta: sessão, entrada, e a saída para o app.
 *
 * ── Por que a fiação mora aqui e não em `ConvitePublico.tsx` ─────────────
 *
 * Porque aquele arquivo é o único do app que não deve tocar em `services/api`
 * nem em sessão: ele desenha telas para quem chega sem conta, e fala com o
 * servidor por `fetch` puro. A tela do convite de meta continua lá, pura,
 * recebendo o que fazer por props — o que precisa de sessão é isto aqui.
 *
 * ── Por que fica DENTRO do `AuthProvider` ────────────────────────────────
 *
 * Diferente das outras duas telas públicas, esta precisa da sessão: ENTRAR na
 * meta cria uma linha com `user_id`, e é ela que vai receber os aportes. Ver o
 * convite continua não custando nada — `PaginaDeConviteDeMeta` busca a meta
 * por `fetch`, sem `Authorization`, antes de qualquer pergunta sobre conta.
 *
 * ── Por que NÃO entra sozinha depois do login ────────────────────────────
 *
 * Seria tentador: a pessoa acabou de clicar em "criar conta e entrar", voltou
 * do Google, e o token está guardado. Um toque a menos.
 *
 * Mas o retorno do OAuth não é um clique dela — é um redirecionamento. Entrar
 * ali faria a volta do Google, sozinha, colocar a pessoa num grupo financeiro;
 * e se ela tivesse desistido no meio, ou o link fosse de outra pessoa na mesma
 * máquina, a entrada aconteceria sem ninguém ter pedido. O botão volta com a
 * sessão pronta dizendo "Entrar na meta", e o consentimento continua sendo um
 * toque.
 */
export const ConviteDeMeta: React.FC<{ token: string }> = ({ token }) => {
  const { session, signInWithGoogle } = useAuth();

  /*
   * `location.replace` e não `assign`: com `assign`, o botão "voltar" traria a
   * pessoa de volta à tela do convite que ela acabou de responder — e o
   * convite já não estaria mais aberto, então ela veria "convite indisponível"
   * como resultado de ter entrado.
   *
   * Vai para a raiz porque o caminho `/convite-meta/<token>` não é mais dela:
   * deixá-lo na barra faria a recarga voltar para cá.
   */
  const paraOApp = () => { window.location.replace('/'); };

  const aceitar = useCallback(async () => {
    await entrarNaMetaPeloToken(token);
    limparConviteDeMeta();
    paraOApp();
  }, [token]);

  const dispensar = useCallback(() => {
    /*
     * Limpa o pendente também aqui. Sem isto, quem clicou em "agora não"
     * voltaria para esta mesma tela no próximo carregamento da aba — é o
     * pendente que faz o `index.tsx` desviar para cá.
     */
    limparConviteDeMeta();
    paraOApp();
  }, []);

  /*
   * O convite que o servidor recusou abrir é esquecido NA HORA — sem levar a
   * pessoa para lugar nenhum.
   *
   * Sem isto ela fica trancada fora do app: o token pendente faz o `index.tsx`
   * desviar para cá em toda visita à raiz, e a tela de "convite indisponível"
   * não tem botão que limpe. Encontrado em produção.
   *
   * Não redireciona porque ela precisa LER o motivo. O link "Conhecer o
   * FinTracking" na própria tela já leva ao app — e agora leva de verdade, em
   * vez de voltar para este mesmo recado.
   */
  const aoNaoAbrir = useCallback(() => { limparConviteDeMeta(); }, []);

  return (
    <PaginaDeConviteDeMeta
      token={token}
      temSessao={!!session}
      entrarComGoogle={signInWithGoogle}
      entradaComSenha={LOGIN_COM_SENHA ? <EntradaComSenha /> : undefined}
      aceitar={aceitar}
      dispensar={dispensar}
      aoNaoAbrir={aoNaoAbrir}
    />
  );
};

export default ConviteDeMeta;
